/**
 * EquipmentAll.jsx — Global equipment tracker (Iter 426.p8).
 *
 * Dirk: *"Equipment page must be the same as the customer-level
 * equipment page but for ALL equipment. Search customer + site,
 * then track and manage equipment and services from there — so we
 * don't need to go into each customer to log services."*
 *
 * How it works:
 *   1. Loads all equipment (owner sees everything, portal role blocked
 *      by the backend scope).
 *   2. Two live filters — customer + site (site list narrows to the
 *      picked customer). Search box for name / SKU / serial / notes.
 *   3. Renders the SAME <EquipmentBlock> the customer profile uses,
 *      passing the filtered slice so every service-log / edit /
 *      schedule action stays identical (no duplicate UI to
 *      maintain).
 *   4. Empty state before a filter is picked shows a quick-scan card
 *      of "due today / this week / overdue" pulled from the full
 *      dataset so ops sees the priority at-a-glance.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Wrench, AlertOctagon, CalendarClock, Search, Filter } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import EquipmentBlock from "../components/customer-blocks/EquipmentBlock";
import SearchableSelect from "../components/SearchableSelect";

export default function EquipmentAll() {
  const { user } = useAuth();
  const canEdit = (user?.role || "").toLowerCase() !== "viewer";
  const [params, setParams] = useSearchParams();
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [serviceLogs, setServiceLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const customerId = params.get("customer") || "";
  const siteId = params.get("site") || "";
  const q = params.get("q") || "";

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    // Site is bound to customer — clear it whenever customer changes.
    if (k === "customer") next.delete("site");
    setParams(next, { replace: true });
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [c, s, e, l] = await Promise.all([
          api.get("/customers").catch(() => ({ data: [] })),
          api.get("/sites").catch(() => ({ data: [] })),
          api.get("/equipment").catch(() => ({ data: [] })),
          api.get("/service-logs?limit=500").catch(() => ({ data: [] })),
        ]);
        if (!alive) return;
        const _cs = c.data?.items || c.data || [];
        const _ss = s.data?.items || s.data || [];
        const _es = e.data?.items || e.data || [];
        const _ls = l.data?.items || l.data || [];
        setCustomers(_cs);
        setSites(_ss);
        setEquipment(_es);
        setServiceLogs(_ls);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Site options narrow when a customer is picked.
  const siteOptions = useMemo(() => {
    const src = customerId
      ? sites.filter((s) => s.customer_id === customerId)
      : sites;
    return [
      { value: "", label: "All sites" },
      ...src.map((s) => ({ value: s.id, label: s.name })),
    ];
  }, [sites, customerId]);

  const customerOptions = useMemo(() => (
    [{ value: "", label: "All customers" },
      ...customers.map((c) => ({
        value: c.id,
        label: c.name || c.trading_as || "—",
      })),
    ]
  ), [customers]);

  // Apply the filters. Search matches name / sku / serial / notes.
  const filtered = useMemo(() => {
    let list = equipment;
    if (customerId) list = list.filter((e) => e.customer_id === customerId);
    if (siteId) list = list.filter((e) => e.site_id === siteId);
    if (q) {
      const needle = q.toLowerCase();
      list = list.filter((e) => (
        (e.name || "").toLowerCase().includes(needle) ||
        (e.sku || "").toLowerCase().includes(needle) ||
        (e.serial_number || "").toLowerCase().includes(needle) ||
        (e.notes || "").toLowerCase().includes(needle) ||
        (e.manufacturer || "").toLowerCase().includes(needle)
      ));
    }
    return list;
  }, [equipment, customerId, siteId, q]);

  // Quick scan of the FULL dataset (before search/filter).
  const scan = useMemo(() => {
    const now = new Date();
    const week = new Date(now.getTime() + 7 * 24 * 3600 * 1000);
    let overdue = 0, thisWeek = 0, total = 0;
    for (const e of equipment) {
      if (!e.is_service_item) continue;
      total += 1;
      if (!e.next_service_date) continue;
      const d = new Date(e.next_service_date);
      if (d < now) overdue += 1;
      else if (d < week) thisWeek += 1;
    }
    return { overdue, thisWeek, total, count: equipment.length };
  }, [equipment]);

  const sitesForBlock = useMemo(() => {
    if (customerId) return sites.filter((s) => s.customer_id === customerId);
    return sites;
  }, [sites, customerId]);
  const activeCustomerId = customerId || null;

  return (
    <div className="hv-container hv-page pb-24">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#647087]">
          Operations
        </div>
        <div className="flex items-center gap-3 mt-1">
          <div className="w-2 h-2 rounded-full bg-[#a78bfa] animate-pulse" />
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
            Equipment
          </h1>
        </div>
        <p className="text-sm text-[#647087] mt-1.5 max-w-2xl">
          Search across every customer and site. Log services, edit
          intervals, and open the site's own equipment view without
          leaving this page.
        </p>
      </div>

      {/* Quick-scan chips */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mb-5" data-testid="equipment-scan-chips">
        <ScanChip
          label="Total equipment"
          value={scan.count}
          icon={Wrench}
          accent="#A78BFA"
        />
        <ScanChip
          label="On service schedule"
          value={scan.total}
          icon={CalendarClock}
          accent="#22D3EE"
        />
        <ScanChip
          label="Overdue"
          value={scan.overdue}
          icon={AlertOctagon}
          accent="#F43F5E"
        />
        <ScanChip
          label="Due within 7 days"
          value={scan.thisWeek}
          icon={CalendarClock}
          accent="#F59E0B"
        />
      </div>

      {/* Filter bar */}
      <div className="hv-card p-3 mb-4 space-y-2.5 md:space-y-0 md:flex md:items-center md:gap-2.5" data-testid="equipment-filter-bar">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 text-[#647087] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="search"
            data-testid="equipment-search"
            className="hv-input pl-9"
            placeholder="Search name, SKU, serial, manufacturer…"
            value={q}
            onChange={(e) => setParam("q", e.target.value)}
          />
        </div>
        <div className="md:w-64">
          <SearchableSelect
            data-testid="equipment-customer-filter"
            value={customerId}
            options={customerOptions}
            onChange={(v) => setParam("customer", v)}
            placeholder="All customers"
          />
        </div>
        <div className="md:w-56">
          <SearchableSelect
            data-testid="equipment-site-filter"
            value={siteId}
            options={siteOptions}
            onChange={(v) => setParam("site", v)}
            placeholder="All sites"
            disabled={!customerId && sites.length > 100}
          />
        </div>
        {(customerId || siteId || q) && (
          <button
            type="button"
            className="hv-btn hv-btn-ghost text-xs"
            onClick={() => setParams({}, { replace: true })}
            data-testid="equipment-clear-filters"
          >
            <Filter className="w-3.5 h-3.5" /> Clear
          </button>
        )}
      </div>

      {/* Result */}
      {loading ? (
        <div className="hv-card p-8 text-center text-sm text-[#647087]" data-testid="equipment-loading">
          Loading equipment…
        </div>
      ) : filtered.length === 0 ? (
        <div className="hv-card p-8 text-center text-sm text-[#647087]" data-testid="equipment-empty">
          {equipment.length === 0
            ? "No equipment recorded yet. Add equipment from a site's Equipment tab."
            : "No equipment matches the current filters."}
        </div>
      ) : (
        <EquipmentBlock
          sites={sitesForBlock}
          equipment={filtered}
          serviceLogs={serviceLogs}
          customerId={activeCustomerId}
          canEdit={canEdit}
          onChanged={async () => {
            const [e, l] = await Promise.all([
              api.get("/equipment").catch(() => ({ data: [] })),
              api.get("/service-logs?limit=500").catch(() => ({ data: [] })),
            ]);
            setEquipment(e.data?.items || e.data || []);
            setServiceLogs(l.data?.items || l.data || []);
          }}
        />
      )}
    </div>
  );
}

function ScanChip({ label, value, icon: Icon, accent }) {
  return (
    <div
      className="hv-card p-3 flex items-center gap-3"
      style={{ borderColor: `${accent}40`, background: `${accent}0d` }}
    >
      <div
        className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
        style={{ background: `${accent}22`, color: accent }}
      >
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-[#647087] leading-tight">
          {label}
        </div>
        <div className="font-display text-xl font-semibold text-white leading-tight">
          {value}
        </div>
      </div>
    </div>
  );
}
